import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TacticalButton } from '@/components/ui/TacticalButton'
import { TacticalCard } from '@/components/ui/TacticalCard'
import { TacticalInput } from '@/components/ui/TacticalInput'
import { TacticalSelect } from '@/components/ui/TacticalSelect'
import type { UserLicense } from '@/hooks/useAdmin'
import { formatDate } from '@/hooks/useAdmin'

const LICENSE_TIERS = [
  { value: 'LIC-TRIAL', label: 'Trial' },
  { value: 'LIC-MVPS', label: 'Micro VPS' },
  { value: 'LIC-10', label: 'LIC-10 (10 devices)' },
  { value: 'LIC-100', label: 'LIC-100 (100 devices)' },
  { value: 'LIC-500', label: 'LIC-500 (500 devices)' },
  { value: 'LIC-1000', label: 'LIC-1000 (1000 devices)' },
]

const LICENSE_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'cancelled', label: 'Cancelled' },
]

type OrgOption = { id: string; name: string; owner_id: string }

const LICENSE_DEFAULTS: Record<string, { devices: number; quota: number | null }> = {
  'LIC-TRIAL': { devices: 2, quota: 100 },
  'LIC-MVPS': { devices: 1, quota: 0 },
  'LIC-10': { devices: 10, quota: 25_000 },
  'LIC-100': { devices: 100, quota: 250_000 },
  'LIC-500': { devices: 500, quota: null },
  'LIC-1000': { devices: 1000, quota: null },
}

type LicenseManagerProps = {
  licenses: UserLicense[]
  userId: string
  organization?: OrgOption
  isLoading?: boolean
  onUpdateLicense: (licenseId: string, updates: Partial<UserLicense>) => void
  onCreateLicense: (license: {
    user_id: string
    license_tier: string
    total_devices: number
    annual_quota_km2: number
    org_id?: string | null
  }) => void
  isUpdating?: boolean
  isCreating?: boolean
}

export function LicenseManager({
  licenses,
  userId,
  organization,
  isLoading,
  onUpdateLicense,
  onCreateLicense,
  isUpdating,
  isCreating,
}: LicenseManagerProps) {
  const [editingLicense, setEditingLicense] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)

  // Edit form state
  const [editForm, setEditForm] = useState<{
    annual_quota_km2: number
    total_devices: number
    status: string
  } | null>(null)

  // Create form state
  const [createForm, setCreateForm] = useState({
    license_tier: 'LIC-10',
    total_devices: 10,
    annual_quota_km2: 25_000,
  })

  const startEditing = (license: UserLicense) => {
    setEditingLicense(license.id)
    setEditForm({
      annual_quota_km2: license.annual_quota_km2,
      total_devices: license.total_devices,
      status: license.status === 'expired' ? 'active' : license.status,
    })
  }

  const cancelEditing = () => {
    setEditingLicense(null)
    setEditForm(null)
  }

  const saveEditing = () => {
    if (!editingLicense || !editForm) return

    onUpdateLicense(editingLicense, {
      annual_quota_km2: editForm.annual_quota_km2,
      total_devices: editForm.total_devices,
      status: editForm.status,
    })
    setEditingLicense(null)
    setEditForm(null)
  }

  const handleCreate = () => {
    const isMvps = createForm.license_tier === 'LIC-MVPS'
    onCreateLicense({
      user_id: organization?.owner_id ?? userId,
      license_tier: createForm.license_tier,
      total_devices: isMvps ? 1 : createForm.total_devices,
      annual_quota_km2: isMvps ? 0 : createForm.annual_quota_km2,
      org_id: organization?.id ?? null,
    })
    setShowCreateForm(false)
    setCreateForm({
      license_tier: 'LIC-10',
      total_devices: 10,
      annual_quota_km2: 25_000,
    })
  }

  const selectedDefaults = LICENSE_DEFAULTS[createForm.license_tier]
  const requiresContractQuota = selectedDefaults?.quota === null
  const createDisabled = isCreating || (
    createForm.license_tier !== 'LIC-MVPS' &&
    (createForm.total_devices <= 0 || createForm.annual_quota_km2 <= 0)
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground/70">
          {licenses.length} license{licenses.length !== 1 ? 's' : ''}
        </span>
        {!showCreateForm && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="text-xs text-primary hover:text-primary/80 transition-colors"
          >
            + Add license
          </button>
        )}
      </div>

      {/* Create License Form */}
      {showCreateForm && (
        <TacticalCard variant="accent" padding="default">
          <p className="text-xs text-muted-foreground/70 mb-4">New license</p>
          <p className="mb-4 text-xs text-muted-foreground">
            Scope: {organization ? organization.name : 'Personal'}
          </p>
          <div className="grid grid-cols-2 gap-4">
            <TacticalSelect
              label="Tier"
              value={createForm.license_tier}
              onChange={(e) => {
                const defaults = LICENSE_DEFAULTS[e.target.value]
                setCreateForm({
                  ...createForm,
                  license_tier: e.target.value,
                  total_devices: defaults.devices,
                  annual_quota_km2: defaults.quota ?? 0,
                })
              }}
              options={LICENSE_TIERS}
            />
            {createForm.license_tier !== 'LIC-MVPS' && (
              <TacticalInput
                label="Devices"
                type="number"
                value={createForm.total_devices}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    total_devices: parseInt(e.target.value) || 0,
                  })
                }
              />
            )}
            {requiresContractQuota && (
              <p className="col-span-2 text-xs text-amber-400/80">
                This tier has no standard map quota. Enter the value from the customer contract.
              </p>
            )}
            {createForm.license_tier === 'LIC-MVPS' ? (
              <div className="flex flex-col">
                <label className="text-xs text-muted-foreground/70 mb-1.5">Quota</label>
                <span className="text-sm text-foreground py-2">Unlimited</span>
              </div>
            ) : (
              <TacticalInput
                label="Quota (km²)"
                type="number"
                value={createForm.annual_quota_km2}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    annual_quota_km2: parseInt(e.target.value) || 0,
                  })
                }
              />
            )}
          </div>
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-border/10">
            <button
              onClick={() => setShowCreateForm(false)}
              disabled={isCreating}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <TacticalButton size="sm" onClick={handleCreate} disabled={createDisabled}>
              {isCreating && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
              Create
            </TacticalButton>
          </div>
        </TacticalCard>
      )}

      {/* License List */}
      {licenses.length === 0 && !showCreateForm ? (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground/70">No licenses</p>
        </div>
      ) : (
        <div className="space-y-3">
          {licenses.map((license) => (
            <LicenseCard
              key={license.id}
              license={license}
              organizationName={organization?.name}
              isEditing={editingLicense === license.id}
              editForm={editingLicense === license.id ? editForm : null}
              onEditFormChange={setEditForm}
              onStartEdit={() => startEditing(license)}
              onCancelEdit={cancelEditing}
              onSaveEdit={saveEditing}
              isUpdating={isUpdating}
            />
          ))}
        </div>
      )}
    </div>
  )
}

type LicenseEditForm = {
  annual_quota_km2: number
  total_devices: number
  status: string
}

type LicenseCardProps = {
  license: UserLicense
  organizationName?: string
  isEditing: boolean
  editForm: LicenseEditForm | null
  onEditFormChange: (form: LicenseEditForm) => void
  onStartEdit: () => void
  onCancelEdit: () => void
  onSaveEdit: () => void
  isUpdating?: boolean
}

function LicenseCard({
  license,
  organizationName,
  isEditing,
  editForm,
  onEditFormChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  isUpdating,
}: LicenseCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'expired':
        return 'text-primary/70'
      case 'cancelled':
        return 'text-destructive/70'
      default:
        return 'text-muted-foreground/50'
    }
  }

  const isMvps = license.license_tier === 'LIC-MVPS'

  if (isEditing && editForm) {
    return (
      <TacticalCard variant="accent" padding="default">
        <div className="grid grid-cols-2 gap-4">
          {!isMvps && (
            <TacticalInput
              label="Devices"
              type="number"
              value={editForm.total_devices}
              onChange={(e) =>
                onEditFormChange({
                  ...editForm,
                  total_devices: parseInt(e.target.value) || 0,
                })
              }
            />
          )}
          {isMvps ? (
            <div className="flex flex-col">
              <label className="text-xs text-muted-foreground/70 mb-1.5">Quota</label>
              <span className="text-sm text-foreground py-2">Unlimited</span>
            </div>
          ) : (
            <TacticalInput
              label="Quota (km²)"
              type="number"
              value={editForm.annual_quota_km2}
              onChange={(e) =>
                onEditFormChange({
                  ...editForm,
                  annual_quota_km2: parseInt(e.target.value) || 0,
                })
              }
            />
          )}
          <TacticalSelect
            label="Status"
            value={editForm.status}
            onChange={(e) => onEditFormChange({ ...editForm, status: e.target.value })}
            options={LICENSE_STATUSES}
          />
          <div className="text-xs text-muted-foreground">Scope: {organizationName ?? 'Personal'}</div>
        </div>
        <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-border/10">
          <button
            onClick={onCancelEdit}
            disabled={isUpdating}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <TacticalButton size="sm" onClick={onSaveEdit} disabled={isUpdating}>
            {isUpdating && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
            Save
          </TacticalButton>
        </div>
      </TacticalCard>
    )
  }

  return (
    <TacticalCard variant="default" padding="default">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground">{license.license_tier}</p>
            <span className={cn('text-xs', getStatusColor(license.status))}>{license.status === 'expired' ? 'active' : license.status}</span>
            {license.org_id && (
              <span className="text-xs text-primary/70">
                · {organizationName ?? 'Organization'}
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-muted-foreground/50">
            {!isMvps && (
              <>
                <span>Devices: {license.total_devices}</span>
                <span className="mx-2">·</span>
              </>
            )}
            <span>Quota: {isMvps ? 'Unlimited' : `${license.annual_quota_km2} km²`}</span>
          </div>
          <p className="text-xs text-muted-foreground/50 mt-1">
            {formatDate(license.purchased_at)} — Perpetual
          </p>
        </div>

        <button
          onClick={onStartEdit}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
        >
          Edit
        </button>
      </div>
    </TacticalCard>
  )
}
